import type * as z from 'zod/mini';

import { getCore } from '../../../../client/core/getCore';
import type { DynamicClient } from '../../../../client/types';
import { createStorageKeySchema } from '../../../../services/storage';
import { assertDefined } from '../../../../utils/assertDefined';
import { p256Keygen } from '../../../../utils/p256Keygen';
import { deviceSigningKeysSchema } from '../deviceSigningKeys.schema';

/**
 * On web we want to auto create the device signing keys for each user.
 * And the user device signing keys should never be deleted.
 */
export const getOrCreateEnvironmentDeviceSigningKeys = async (
  client: DynamicClient
): Promise<z.infer<typeof deviceSigningKeysSchema>> => {
  const { storage, environmentId } = getCore(client);

  assertDefined(environmentId, 'Environment ID is required');

  const environmentDeviceSigningKeysStorageKeySchema = createStorageKeySchema({
    key: `user_device_signing_keys_${environmentId}`,
    schema: deviceSigningKeysSchema,
  });

  const deviceSigningKeys = await storage.getItem(
    environmentDeviceSigningKeysStorageKeySchema
  );

  if (deviceSigningKeys) {
    return deviceSigningKeys;
  }

  const {
    public: publicKey,
    private: privateKey,
    privateJwk,
  } = await p256Keygen();

  await storage.setItem(environmentDeviceSigningKeysStorageKeySchema, {
    privateJwk,
    privateKey,
    publicKey,
  });

  return {
    privateJwk,
    privateKey,
    publicKey,
  };
};
